import { ChevronLeft } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, Outlet, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { TabNav } from "@/components/TabNav";
import { useApi } from "@/lib/api";
import { hasRole } from "@/lib/hasRole";
import { plural } from "@/lib/plural";

interface Team {
  id: string;
  name: string;
  members: { userId: string }[];
}

export interface TeamLayoutContext {
  teamName: string;
  onTeamRenamed: (name: string) => void;
  onTeamChanged: () => void;
}

export function TeamLayout() {
  const { accessToken, user } = useAuth();
  const api = useApi();
  const { orgId, teamId } = useParams();
  const [team, setTeam] = useState<Team | null>(null);
  const [ruleCount, setRuleCount] = useState<number | null>(null);

  const isAdmin = hasRole(user?.roles, "Admin");

  const fetchTeam = useCallback(async () => {
    if (!accessToken || !orgId || !teamId) {
      return null;
    }

    const [teamData, rules] = await Promise.all([
      api.get<Team>(`/organizations/${orgId}/teams/${teamId}`),
      api.get<unknown[]>(
        `/organizations/${orgId}/teams/${teamId}/access-rules`,
      ),
    ]);

    return { team: teamData, ruleCount: rules?.length ?? null };
  }, [accessToken, api, orgId, teamId]);

  const apply = useCallback(
    (data: { team: Team | null; ruleCount: number | null } | null) => {
      if (!data) {
        return;
      }

      setTeam(data.team);
      setRuleCount(data.ruleCount);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    void fetchTeam().then((data) => {
      if (!cancelled) {
        apply(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchTeam, apply]);

  const refresh = useCallback(
    async () => apply(await fetchTeam()),
    [apply, fetchTeam],
  );

  function onTeamRenamed(name: string) {
    setTeam((current) => (current ? { ...current, name } : current));
  }

  return (
    <div className="mx-auto max-w-3xl text-foreground">
      <Link
        to={`/${orgId}/teams`}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-accent"
      >
        <ChevronLeft className="h-3 w-3" strokeWidth={3.5} aria-hidden="true" />
        Teams
      </Link>

      {team ? (
        <div className="mb-4">
          <h1 className="text-2xl font-semibold tracking-tight">{team.name}</h1>
          <p className="mt-0.5 text-sm text-foreground/70">
            {plural(team.members.length, "member")}
            {ruleCount !== null && ` · ${plural(ruleCount, "access rule")}`}
          </p>
        </div>
      ) : (
        <div className="mb-4 animate-pulse">
          <div className="h-7 w-52 rounded bg-surface" />
          <div className="mt-1.5 h-3.5 w-32 rounded bg-surface" />
        </div>
      )}

      <TabNav
        tabs={[
          { label: "Members", to: "members", count: team?.members.length },
          {
            label: "Access rules",
            to: "access-rules",
            count: ruleCount ?? undefined,
          },
          ...(isAdmin ? [{ label: "Settings", to: "settings" }] : []),
        ]}
        basePath={`/${orgId}/teams/${teamId}`}
      />

      <Outlet
        context={
          {
            teamName: team?.name ?? "",
            onTeamRenamed,
            onTeamChanged: refresh,
          } satisfies TeamLayoutContext
        }
      />
    </div>
  );
}
